/**
 * CheckoutPage - 체크아웃(주문) 페이지
 *
 * 기능:
 * - 장바구니 아이템 요약 표시
 * - 배송/픽업 정보 입력
 * - 팁 선택
 * - 포인트 사용 (로그인 시)
 * - 저장 카드 선택 및 결제
 * - 결제 프로세스 시작 (실제 API 연동)
 *
 * @see #131 - 검증 로직 함수화
 * @see #132 - ErrorBoundary 추가
 * @see #135 - handleProceedToPayment 복잡도 개선
 * @see #236 - 체크아웃 재설계: 팁/포인트/카드 선택 추가
 */
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Info } from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { usePaymentFlow } from '@/contexts/CheckoutContext';
import { useOrderHistory } from '@/contexts/OrderHistoryContext';
import { useStorePricing } from '@/contexts/StorePricingContext';
import { checkoutTranslations, a11yTranslations, savedCardsTranslations } from '@/i18n';
import { ROUTES, getRestaurantPath, getRestaurantTablePath } from '@/config/routes';
import { formatCurrency } from '@/utils';
import { orderApi, paymentApi } from '@/lib/api';
import type {
  BloominCardRegistrationPayload,
  CreateOrderRequest,
  Payment,
  SavedCard,
} from '@/lib/api/types';
import { SUPPORTED_ORDER_TYPES } from '@/constants/validation';
import { useSafeArea } from '@/hooks/useSafeArea';
import { toast } from 'sonner';
import { getOrCreateGuestPassword, saveGuestPhone } from '@/utils/password';
import { logger } from '@/utils/logger';
import {
  validateCheckoutForm,
  validateStoreId,
  convertAndValidateOrderItems,
  getCheckoutErrorMessage,
  createPaymentData,
} from '@/utils/checkout';
import { usePaymentMethodCombinations } from '@/hooks/usePaymentMethodCombinations';
import { useStoreAcceptedPaymentMethods } from '@/hooks/useStoreAcceptedPaymentMethods';
import {
  computeAvailableCombinations,
  computeMethodSupport,
  isPlatformCardCombination,
  type PaymentFilterOrderType,
} from '@/lib/payments/combinations';
import { isTrustedBloominChallengeUrl } from '@/lib/payments/bloominChallenge';
import { ErrorBoundary, CheckoutErrorFallback } from '@/components/ErrorBoundary';
import { usePaymentCalculations } from '@/hooks/usePaymentCalculations';
import { ApiClientError } from '@/lib/api/client';
import { readWaitlistToken } from '@/components/waitlist/storage';
import {
  clearOrphanOrder,
  computeCartSignature,
  getOrphanOrder,
  saveOrphanOrder,
} from '@/lib/orphan-order/storage';
import {
  getCheckoutTableSessionFromStorage,
  getTableSessionIdentity,
  persistTableSessionIdentity,
  type CheckoutTableSession,
} from '@/lib/tableContext';
import { useSavedCards } from '@/hooks/useSavedCards';
import { useAvailablePoints } from '@/hooks/useAvailablePoints';
import { useEstimatedPoints } from '@/hooks/useEstimatedPoints';
import { CheckoutItemsList } from '@/components/checkout/CheckoutItemsList';
import { TipSelector } from '@/components/checkout/TipSelector';
import { CheckoutSummary } from '@/components/checkout/CheckoutSummary';
import { PointsSection } from '@/components/checkout/PointsSection';
import { SavedCardBottomPanel } from '@/components/checkout/SavedCardBottomPanel';
import { AddCardModal } from '@/components/payment/AddCardModal';
import { LoginPromptSheet } from '@/components/checkout/LoginPromptSheet';
import { OrderSuccessScreen, type OrderSuccessSummary } from '@/components/checkout/OrderSuccessScreen';
import type { GuestPushContext } from '@/lib/push';
import { floorMoneyByRatio, minMoney, subtractMoney } from '@/utils/moneyMinor';

const SESSION_KEY = 'checkout_login_prompt_shown';
type CompletionNotice = 'postpay' | 'prepay_manual';

function shouldPromptLoginForAuthExpiry(error: unknown, wasLoggedIn: boolean): boolean {
  if (!(error instanceof ApiClientError) || error.status !== 401) {
    return false;
  }

  return wasLoggedIn || error.code === 'UNAUTHENTICATED';
}

export function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { locale } = useApp();
  const { isLoggedIn, isCheckingSession, user } = useAuth();
  const { cartItems, clearCart } = useCart();
  const { handlePaymentSuccess } = usePaymentFlow();
  const {
    saveGuestCredentials,
    refetch: refetchOrderHistory,
    createOrderFromCheckout,
  } = useOrderHistory();
  const t = checkoutTranslations[locale];
  const addCardT = savedCardsTranslations[locale];
  const { bottomCSS: safeAreaBottom } = useSafeArea();

  // 폼 상태 (#263: 로그인 사용자 정보는 useEffect에서 단일 경로로 채움)
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');

  // #272 Q8 옵션 B: 로그인 사용자 전용 대체 연락처 토글.
  // - 게스트는 phone 자체를 자유 편집하므로 대체 연락처 의미 없음 → 토글 미노출.
  // - 토글 OFF 또는 contactPhone 빈 값 → payload 미포함 (BE 가 customer_phone fallback).
  const [useAlternateContact, setUseAlternateContact] = useState(false);
  const [contactPhone, setContactPhone] = useState('');

  // #1430 FE root cause 시정:
  // - cart → `navigate('/checkout')` 시 URL 이 `/checkout` 으로 변경 → path `/table/{n}` 매칭 실패
  // - isValidTableContext (path 검증 강제) 가 false 반환 → orderType silent fallback 'pickup'
  // - BE 가 pickup 으로 정상 저장 → 사용자 보고 "테이블 오더가 pickup 으로 들어감"
  //
  // 시정:
  // - 표준 경로 `/restaurant/{id}/table/{n}/checkout` 는 sessionStorage 3 키와 URL table number
  //   일치까지 검증해 table 유지.
  // - 레거시 `/checkout` 은 sessionStorage 3 키 (token + table_number + table_store_name) 모두 검증.
  // - PickupMenu 가 픽업 매장 진입 시 (table_number / table_store_name) 을 clear 하므로
  //   legacy checkout 에서는 stale token 만 남아도 null 반환 → stale token 오염 방지 (#367 박제 정합 보존).
  // 박제 D 단일 출처: src/lib/tableContext.ts
  const [tableSessionState] = useState<CheckoutTableSession | null>(() => {
    if (typeof window === 'undefined') return null;
    return getCheckoutTableSessionFromStorage(location.pathname);
  });
  const orderType: 'pickup' | 'delivery' | 'table' =
    tableSessionState ? 'table' : 'pickup';

  // #363/#1430/#413: table checkout 은 검증된 sessionStorage table_number 를 단일 출처로 사용한다.
  const tableNumber = useMemo<string | undefined>(() => {
    if (orderType !== 'table') return undefined;
    return tableSessionState?.tableNumber;
  }, [orderType, tableSessionState]);

  // API 호출 상태 (useEffect가 paymentLockRef를 참조하므로 선언 순서 주의)
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompletingOrder, setIsCompletingOrder] = useState(false);
  const paymentLockRef = useRef(false);

  // #263: 로그인 사용자 정보 prefill
  // - user가 로드되면 빈 필드만 user 정보로 자동 채움 (사용자 편집값은 보존)
  // - user가 null이 되는 경우(자연 세션 만료 포함): 입력값 유지 → 사용자 경험 보호
  //   체크아웃 화면은 마운트 후에도 인증 API(저장 카드, 포인트, 적립 예정 포인트)를 계속
  //   호출하므로 입력 중/검토 중 401이 발생해도 폼이 비워지면 UX 리그레션.
  //   명시적 로그아웃 시 폼 잔존 우려는 일반적으로 화면 전환(홈 리다이렉트)으로 해소됨.
  useEffect(() => {
    if (!user) return;
    setName((prev) => prev || (user.name ?? ''));
    setPhone((prev) => prev || (user.phone_number ?? ''));
  }, [user]);

  // #628 F-07: 비회원 테이블 재주문은 같은 QR 세션에서 이전 선택 입력을 재사용한다.
  // Auth session 확인 중에는 guest identity 를 적용하지 않아 로그인 사용자 prefill 과 섞이지 않게 한다.
  useEffect(() => {
    if (isCheckingSession || isLoggedIn || user || orderType !== 'table') return;

    const identity = getTableSessionIdentity(tableSessionState);
    if (!identity) return;

    setName((prev) => prev || identity.name);
    setPhone((prev) => prev || identity.phone);
  }, [isCheckingSession, isLoggedIn, orderType, tableSessionState, user]);

  // 팁 상태
  const [selectedTip, setSelectedTip] = useState<number>(0);
  const [customTip, setCustomTip] = useState<string>('');
  const [customTipMode, setCustomTipMode] = useState<'percentage' | 'amount'>('percentage');

  // 포인트 상태
  const [pointsToUse, setPointsToUse] = useState<number>(0);

  // 카드 선택 상태
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
  const [isAddCardModalOpen, setIsAddCardModalOpen] = useState(false);

  // 로그인 유도 바텀시트
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showOrderSuccess, setShowOrderSuccess] = useState(false);

  // #603 [1]: 주문 완료 페이지용 "방금 주문" 요약 — cart clear 전에 캡처.
  // clearCart 후 cartItems 가 비워지므로 storeId/itemCount/total 을 주문 시점에 박제한다.
  const [justOrderedSummary, setJustOrderedSummary] = useState<OrderSuccessSummary | null>(null);
  // #694: 비회원 푸시 귀속 컨텍스트 — 주문 완료 시점에 구성해 OrderSuccessScreen 으로 전달.
  // guest_password 는 메모리(컴포넌트 state)로만 전달 — localStorage 등 추가 영속화 금지.
  const [guestPushContext, setGuestPushContext] = useState<GuestPushContext | null>(null);
  // 테이블 "메뉴 더 보기" 복귀 경로 — clearCart 후 storeId 가 사라지므로 미리 캡처.
  const [tableMenuReturnPath, setTableMenuReturnPath] = useState<string | null>(null);

  // 뒤로가기
  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // 로그인 유도 바텀시트 핸들러
  const handleDismissLoginPrompt = useCallback(() => {
    setShowLoginPrompt(false);
    sessionStorage.setItem(SESSION_KEY, '1');
  }, []);

  const handleGoLogin = useCallback(() => {
    setShowLoginPrompt(false);
    sessionStorage.setItem(SESSION_KEY, '1');
    navigate(`${ROUTES.LOGIN}?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`);
  }, [navigate]);

  // #603 [1] + #626: 테이블·픽업 모두 주문 완료 페이지(OrderSuccessScreen)를 경유한다.
  // (기존: 테이블은 즉시 /order-history 리다이렉트 → 고객이 "주문 접수됨"을 인지 못함.)
  // - 테이블(#626): 백그라운드로 주문 내역을 미리 갱신해 두고(회원), 완료 화면 표시 후
  //   5초 카운트다운 자동으로 메뉴 화면 복귀(추가 주문 유도). "메뉴 더 보기"로 즉시 이동 가능.
  const navigateAfterCompletedOrder = useCallback(async (orderId: number, _notice?: CompletionNotice, guestPassword?: string) => {
    if (orderType === 'table' && isLoggedIn) {
      await refetchOrderHistory().catch((error) => {
        logger.warn('테이블 오더 주문 내역 갱신 실패:', error);
      });
    }

    // #694: 비회원 푸시 귀속 컨텍스트 구성 — 테이블 = 세션 단위(결정 2), 픽업 = 주문 단위.
    // 테이블 판정은 단일 출처 getTableSessionFromStorage 결과(tableSessionState — 마운트 시 캡처) 기준.
    if (!isLoggedIn) {
      if (orderType === 'table' && tableSessionState) {
        setGuestPushContext({ kind: 'table-session', sessionToken: tableSessionState.token });
      } else if (guestPassword) {
        setGuestPushContext({ kind: 'order', orderId, guestPassword });
      }
    }

    setShowOrderSuccess(true);
  }, [isLoggedIn, orderType, refetchOrderHistory, tableSessionState]);

  // 픽업 primary CTA — 주문 내역 보기.
  const handleViewCompletedOrders = useCallback(() => {
    navigate(ROUTES.ORDERS, { replace: true });
  }, [navigate]);

  // 픽업 secondary CTA — 홈으로.
  const handleGoHome = useCallback(() => {
    navigate(ROUTES.HOME, { replace: true });
  }, [navigate]);

  // 테이블 primary CTA — 메뉴 더 보기(계속 주문) + #626 5초 자동 이동의 공용 목적지.
  // 캡처해 둔 테이블 메뉴 경로로 복귀. 경로 캡처에 실패한 예외적 경우(매장 식별 불가)에는
  // 주문 현황으로 폴백(fail-safe).
  // #626: 테이블 secondary CTA("주문 현황 보기")는 사용 빈도가 낮아 제거.
  const handleContinueTableOrder = useCallback(() => {
    navigate(tableMenuReturnPath ?? ROUTES.ORDER_HISTORY, { replace: true });
  }, [navigate, tableMenuReturnPath]);

  // 장바구니가 비어있으면 즉시 홈으로 리다이렉트
  useEffect(() => {
    if (isCompletingOrder || showOrderSuccess) return;

    if (cartItems.length === 0) {
      navigate(ROUTES.HOME, { replace: true });
    }
  }, [cartItems.length, isCompletingOrder, navigate, showOrderSuccess]);

  // 비로그인 시 로그인 유도 바텀시트 (세션 1회)
  useEffect(() => {
    if (isLoggedIn) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;
    const timer = setTimeout(() => {
      setShowLoginPrompt(true);
    }, 300);
    return () => clearTimeout(timer);
  }, [isLoggedIn]);

  // storeId from cart (NaN 방어: 비숫자 restaurantId 대응)
  const rawStoreId = cartItems[0]?.restaurantId ? Number(cartItems[0].restaurantId) : undefined;
  const storeId = rawStoreId !== undefined && !Number.isNaN(rawStoreId) ? rawStoreId : undefined;

  // 저장 카드 (로그인 시)
  const {
    usableCards,
    defaultCard,
    isLoading: isLoadingCards,
    checkout,
    isCreatingCheckout,
    isConfirming,
    createCheckout,
    confirmBloominRegistration,
  } = useSavedCards({ autoFetch: isLoggedIn });

  // 기본 카드 자동 선택
  useEffect(() => {
    if (defaultCard && !selectedCardId) {
      setSelectedCardId(defaultCard.id);
    }
  }, [defaultCard, selectedCardId]);

  // #285: 매장 포인트 정책 게이트
  const { pointsEnabled, pointsConfig } = useStorePricing();

  // #217/#218 β-2b: 결제 조합 카탈로그 + 매장 허용 조합
  // #421: loading 상태 추가 — 초기 fetch 동안 cashSupported/cardSupported 가 false 로
  // derive 되어 비로그인 분기에서 버튼 hide + 미지원 문구 깜빡임 방지.
  const { combinations: allCombinations, loading: isLoadingCombinations } =
    usePaymentMethodCombinations();
  const { assignments: acceptedAssignments, loading: isLoadingAssignments, platformCardPrepayAvailability } =
    useStoreAcceptedPaymentMethods(storeId ?? null);
  const isLoadingPaymentMethods = isLoadingCombinations || isLoadingAssignments;

  /**
   * #1705 R7: 결제 조합 필터에 적용할 주문유형(pickup/table).
   * delivery 는 SUPPORTED_ORDER_TYPES 에서 pickup 으로 폴백되므로 동일하게 정규화 —
   * admin 의 OrderType(pickup/table) 계약과 정합.
   */
  const paymentFilterOrderType: PaymentFilterOrderType =
    orderType === 'table' ? 'table' : 'pickup';

  /**
   * β-3b: 매장이 허용한 모든 조합 (선불 + 후불). β-2b 의 prepay 필터 제거.
   * handleProceedToPayment 가 method 타입 ('card'|'cash') 에 맞춰 자동 resolve —
   * sort_order ASC 첫 매치로 prepay 우선 선택 (admin PaymentController 분기와 정합).
   *
   * #1705 R7: admin R1 계약 소비 — 각 할당의 order_type scope 가 'all' 이거나 현재
   * 주문유형과 일치하는 조합만 노출. 픽업=선불 / 테이블=후불 매장이 각 유형에 맞는
   * 결제 UI(선불 게이트웨이 / 후불 await_customer)로 정확히 분기된다.
   */
  const availableCombinations = useMemo(() => {
    return computeAvailableCombinations(
      allCombinations,
      acceptedAssignments,
      paymentFilterOrderType,
    );
  }, [allCombinations, acceptedAssignments, paymentFilterOrderType]);

  /**
   * #312 / #1705 SP2-1: postpay × points UI 계약 (주석 갱신 2026-06-29).
   * order-time(주문 생성) 에서 admin 은 postpay 조합 + points_amount > 0 요청을
   * POSTPAY_POINTS_AT_SETTLEMENT (422) 로 식후 결제(/charge)에 라우팅한다
   * (#1705 reserve/capture/release 로 settlement-시점 포인트가 BE 에서 해제됨).
   * → 따라서 order-time 인 본 화면에서는 prepay 조합 부재(후불 전용) 매장의 PointsSection 을 미노출하고,
   *   후불 포인트는 식후 세션 결제(SessionChargePanel)에서 사용한다.
   * (현금 결제 포인트는 별개의 영구 설계 제약 — POINTS_NOT_SUPPORTED.)
   */
  const hasPrepayOption = useMemo(
    () => availableCombinations.some((c) => c.timing === 'prepay'),
    [availableCombinations],
  );
  const hasPostpayOption = useMemo(
    () => availableCombinations.some((c) => c.timing === 'postpay'),
    [availableCombinations],
  );
  const isPostpayOnlyStore = hasPostpayOption && !hasPrepayOption;

  /**
   * #666 (사용자 결정 A안): 후불 테이블 체크아웃 단일 '주문하기' 모드.
   *
   * 후불 테이블 주문에서 주문 시점의 카드/현금 선택은 이 시스템에서 **구속력 없는 힌트**다:
   *  - 카드 선택 주문도 강제마감 현장정산에서 `actual_combination`=현금으로 정산 가능 (배포됨)
   *  - 현금 선택 주문도 세션 합산청구('전체 결제')가 timing=postpay 전부를 카드로 결제 (배포됨)
   * → 실제 수단은 결제 시점(고객 앱 카드 / 현장 현금)에 확정되므로, 고객에게 의미 없는
   *   수단 선택을 강요하던 이중 버튼을 단일 '주문하기'로 줄인다.
   * 선불(픽업)은 주문 시점 결제라 수단 선택이 실제 결정 → 기존 흐름 무변경.
   * 후불 조합 0개면 isPostpayOnlyStore=false → 기존 fail-closed 경로 보존.
   */
  const isPostpayTableCheckout = orderType === 'table' && isPostpayOnlyStore;

  /**
   * #603 [4] 설계 §4: 후불 테이블오더 주문시점 팁 제거 (팁은 식후 세션 결제 [4] 로 이동).
   *
   * 후불 전용 테이블 매장에서만 주문 전 TipSelector 를 숨긴다 — 식사 전 팁 선택은 부자연스럽고,
   * 식후 ChargeSessionModal 에서 세션 소계 기준으로 팁을 받는다(단일 경로). 선불/픽업은 유지.
   */
  const hideOrderTimeTip = isPostpayTableCheckout;

  // 매장이 허용한 결제 method 별 가용성 (사전 차단용 — fail-closed 안전망은 handleProceedToPayment 유지)
  // #421 [2]: method 별 `some(method === ...)` 리터럴 중복 → computeMethodSupport
  // Map 패턴 단일화. 신규 method(예: transfer) 추가 시 combinations.ts 의
  // PAYMENT_METHOD_KINDS 한 곳만 갱신하면 지원 여부 derive 에 자동 포함된다.
  // `?? false`: Map 미등재 kind 는 fail-closed (기존 some() 과 동일 의미).
  const methodSupport = useMemo(
    () => computeMethodSupport(availableCombinations),
    [availableCombinations],
  );
  const cashSupported = methodSupport.get('cash') ?? false;
  const cardSupported = methodSupport.get('card') ?? false;
  const cardCtaLabel = isPostpayOnlyStore
    ? `${t.postpayCheckoutButton} (${t.creditCard})`
    : t.proceedToPayment;
  const cashCtaLabel = isPostpayOnlyStore
    ? `${t.postpayCheckoutButton} (${t.cash})`
    : t.cash;

  /**
   * #666: 후불 테이블 단일 '주문하기'의 내부 기본 결제 method 산출.
   *
   * R7(#604) 의 order_type 조합 필터 결과(availableCombinations — 테이블 후불 전용
   * 매장이면 전부 timing=postpay)를 재사용해 우선순위로 method 를 결정한다:
   *  1. card_platform_postpay 활성 → 'card' (고객 앱 '지금 결제' 카드 경로가 살아있는 매장)
   *  2. 현금 후불 활성 → 'cash' (플랫폼 카드 부재 시 현장 정산 기본)
   *  3. 그 외(card_pos_postpay 단독 등) → 'card' (기존 카드 버튼과 동일 경로 보존)
   * 실제 조합 resolve(sort_order ASC)·fail-closed·서버 검증은 handleProceedToPayment
   * 기존 경로 그대로 — 여기서는 "어느 버튼을 눌렀는지"만 내부적으로 대체한다.
   */
  const defaultPostpayMethod = useMemo<'card' | 'cash'>(() => {
    if (availableCombinations.some((c) => c.code === 'card_platform_postpay')) {
      return 'card';
    }
    // #421 [2]: method 지원 여부는 cashSupported(computeMethodSupport derive) 재사용.
    if (cashSupported) {
      return 'cash';
    }
    return 'card';
  }, [availableCombinations, cashSupported]);

  // 결제 금액 계산 (GROSS 모델).
  // #603 [4] 보강2: calculateFinalTotal(훅 내부 팁) 대신 effectiveTipAmount 로 total 직접 산출
  // (hideOrderTimeTip 시 어느 프레임에서도 tip=0 보장) → 미사용이라 구조분해 제외.
  const {
    calculateSubtotal,
    calculateNetPrice,
    calculateTax,
    calculateTipAmount,
  } = usePaymentCalculations({
    currency: 'MXN',
    cartItems,
    selectedTip,
    customTip,
    customTipMode,
    useAllPoints: false,
    pointsToUse,
    availablePoints: 0,
  });

  const netPrice = calculateNetPrice();
  const tax = calculateTax();

  // #603 [4] Codex Important(보강2): 주문시점 팁 파생값 — money-path 단일 권위.
  // 이전(보강1)의 useEffect 상태 리셋은 렌더 후 실행이라 hideOrderTimeTip=true 가 된 첫 렌더
  // 프레임에 직전 팁 값을 한 프레임 보유하는 창이 있어, 그 프레임에 결제 클릭 시 tip leak(이중
  // 과금) 가능했다(Codex 잔여 지적). → 상태 리셋에 의존하지 않고 파생값으로 차단:
  // hideOrderTimeTip 이면 어느 렌더 프레임에서도 tip=0(프레임 창 제거). 주문 body·summary·
  // createPaymentData·총액(grossPlusTip/total)·적립예정 추정이 모두 effectiveTipAmount 를 직접 쓴다.
  // 선불/픽업(hideOrderTimeTip=false)은 calculateTipAmount 결과 그대로 — 무영향.
  const effectiveTipAmount = hideOrderTimeTip ? 0 : calculateTipAmount();

  // #603 [1]: "방금 주문" 요약용 아이템 수량 합계 (장바구니 항목별 quantity 합산).
  const cartItemCount = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.quantity, 0),
    [cartItems],
  );

  // 적립 예정 포인트 추정 (debounce 500ms)
  // #285: 매장 포인트 비활성 시 호출 스킵
  const { data: estimatedPointsData, isLoading: isEstimating } = useEstimatedPoints(
    storeId,
    netPrice + tax,  // grossAmount
    effectiveTipAmount,
    pointsToUse,
    isLoggedIn && pointsEnabled
  );

  // 팁 적용 전 총액 (포인트 할인 전) - 포인트 캡핑용. effectiveTipAmount 기준(money-path 권위).
  // 서버에서 최종 금액 검증하므로 클라이언트 라운딩 불일치는 허용.
  const grossPlusTip = netPrice + tax + effectiveTipAmount;

  // 사용 가능 포인트 (로그인 + 매장 포인트 활성 시).
  // admin PointController::storeAvailable 와 동일하게 현재 주문 금액을 넘겨
  // min_use_points / max_use_ratio / 보유 포인트를 반영한 usable_amount 를 받는다.
  const { data: pointsData, error: pointsError, isLoading: pointsLoading } = useAvailablePoints(
    storeId,
    isLoggedIn && pointsEnabled,
    grossPlusTip,
  );

  // pointsData가 null이 되면 (매장 전환, API 에러 등) 포인트 사용량 리셋
  useEffect(() => {
    if (!pointsData) {
      setPointsToUse(0);
    }
  }, [pointsData]);

  // #312 hot-fix (Codex P1): hasPrepayOption 이 false 로 전환되면 pointsToUse 강제 리셋.
  // postpay 전용 매장 전환 시 PointsSection 언마운트되지만 pointsToUse 잔존 시
  // order-time 결제 생성에 points_amount 로 전송되어 admin POSTPAY_POINTS_AT_SETTLEMENT 422 회귀
  // (후불 포인트는 식후 /charge 경로 전용 — #1705).
  useEffect(() => {
    if (!hasPrepayOption) {
      setPointsToUse(0);
    }
  }, [hasPrepayOption]);

  useEffect(() => {
    if (!pointsData || pointsToUse <= 0) return;

    const maxPointsByPolicy = minMoney(
      pointsData.usableAmount,
      floorMoneyByRatio(grossPlusTip, pointsData.maxUseRatio),
      grossPlusTip,
    );
    if (pointsToUse < pointsData.minUsePoints) {
      setPointsToUse(0);
      return;
    }
    if (pointsToUse > maxPointsByPolicy) {
      setPointsToUse(Math.max(0, maxPointsByPolicy));
    }
  }, [grossPlusTip, pointsData, pointsToUse]);

  // 최종 결제 금액 = GROSS + 유효팁 − 포인트 (calculateFinalTotal 의 훅 내부 팁 대신 effective 사용).
  const total = subtractMoney(grossPlusTip, pointsToUse);
  // 포인트가 주문 총액을 100% 커버하는지.
  const isPointsFullCover = isLoggedIn && pointsToUse > 0 && total <= 0;

  // 결제 진행 (실제 API 호출)
  // overridePoints: 포인트 전액 커버 모드에서 카드 결제 전환 시 포인트를 0으로 강제
  const handleProceedToPayment = useCallback(async (method: 'card' | 'cash', overridePoints?: number) => {
    // β-2b: method 타입 에 해당하는 매장 활성 조합 중 sort_order ASC 첫 매치.
    // 매장 미활성 시 fail-closed (toast + 중단).
    const resolvedCombination = availableCombinations.find(
      (combo) => combo.method === method,
    );
    if (!resolvedCombination) {
      toast.error(
        method === 'card'
          ? (t.cardPaymentUnavailable ?? '이 매장은 카드 결제를 지원하지 않습니다.')
          : (t.cashPaymentUnavailable ?? '이 매장은 현금 결제를 지원하지 않습니다.'),
      );
      return;
    }

    // 1. 폼 검증 (결제 방식은 method 파라미터로 전달)
    const formError = validateCheckoutForm(
      { name, phone, address },
      orderType,
      t
    );
    if (formError) {
      toast.error(formError.message);
      return;
    }

    // 이미 제출 중이면 무시 (useRef로 동기적 차단 — 같은 tick 이벤트 방지)
    if (paymentLockRef.current || isSubmitting) return;
    paymentLockRef.current = true;
    setIsSubmitting(true);

    // #311: catch 에서 참조하기 위해 try 블록 외부 선언
    let createdOrderResponse: Awaited<ReturnType<typeof orderApi.createOrder>> | undefined;
    // rev 4 hot-fix (code-reviewer P1): orphan 재사용 경로 식별 — payment 실패 시 TTL 갱신
    let usedOrphanOrderId: number | undefined;
    let usedOrphanStoreId: number | undefined;
    let usedOrphanCurrency: string | undefined;
    let usedOrphanTotal: number | undefined;

    try {
      // 2. 매장 검증
      const storeResult = validateStoreId(cartItems, t);
      if (!storeResult.success) {
        toast.error(storeResult.error!);
        return;
      }

      const actualPoints = isLoggedIn ? (overridePoints ?? pointsToUse) : 0;
      const safePointsToUse = actualPoints > 0 ? minMoney(actualPoints, grossPlusTip) : 0;
      const platformPrepayUnavailable = platformCardPrepayAvailability === 'unavailable' &&
        storeResult.storeId === storeId && isPlatformCardCombination(resolvedCombination);
      if (platformPrepayUnavailable && subtractMoney(grossPlusTip, safePointsToUse) > 0) {
        toast.error(t.cardPaymentUnavailable);
        return;
      }

      // 3. 주문 아이템 변환 및 검증
      const itemsResult = convertAndValidateOrderItems(cartItems, t);
      if (!itemsResult.success) {
        toast.error(itemsResult.error!);
        return;
      }

      // 4. 비회원 비밀번호 조회 또는 생성 (로그인 사용자는 제외)
      const guestPassword = isLoggedIn ? undefined : getOrCreateGuestPassword();
      const trimmedName = name.trim();
      const trimmedPhone = phone.trim();

      // 4-1. 비회원 전화번호도 저장 (로그인 시 병합용) (#142)
      if (!isLoggedIn) {
        if (orderType === 'table' && tableSessionState) {
          persistTableSessionIdentity(tableSessionState, {
            name: trimmedName,
            phone: trimmedPhone,
          });
        }
        // #673 codex I1: 테이블 phone 은 선택이라 빈 값이 올 수 있다 — 빈 값으로
        // 기존 guest phone 식별자(로그인 병합 키)를 덮어쓰지 않는다 (#647 계약 정합).
        if (trimmedPhone !== '') {
          saveGuestPhone(trimmedPhone);
        }
      }

      // #272 Q8 옵션 B: 로그인 + 토글 ON + 입력 있을 때만 contact_phone 전송.
      // 빈 입력 토글은 무시 (BE 가 effective_contact_phone fallback 처리).
      const trimmedContactPhone = contactPhone.trim();
      const contactPhonePayload =
        isLoggedIn && useAlternateContact && trimmedContactPhone.length > 0
          ? trimmedContactPhone
          : undefined;

      // 5. 주문 생성 API 호출
      const orderRequest: CreateOrderRequest = {
        store_id: storeResult.storeId!,
        order_type: SUPPORTED_ORDER_TYPES[orderType],
        // #363: 테이블 오더 시 URL 경로의 table_number 도 함께 전달.
        table_number: tableNumber,
        items: itemsResult.items!,
        customer_name: trimmedName,
        customer_phone: trimmedPhone,
        contact_phone: contactPhonePayload,
        guest_password: guestPassword,
        notes: deliveryNotes.trim() || undefined,
        // #603 [4] 보강2: 유효팁(effectiveTipAmount) 사용 — hideOrderTimeTip 시 항상 0(미전송).
        tip: effectiveTipAmount > 0 ? effectiveTipAmount : undefined,
        combination_id:
          orderType === 'table' && resolvedCombination.timing === 'postpay'
            ? resolvedCombination.id
            : undefined,
        // #804: 픽업 줄서기 게이트 매장에서만 서버가 검증한다. 게이트 꺼진 매장은
        // 토큰이 실려도 서버가 무시하므로, 픽업 주문에는 항상 보관 토큰을 첨부한다.
        waitlist_token:
          orderType === 'pickup' && storeResult.storeId !== undefined
            ? (readWaitlistToken(storeResult.storeId) ?? undefined)
            : undefined,
      };

      // #311: orphan order 재시도 가드 — 동일 cart 매칭 시 createOrder skip.
      // #1705 R7 (Codex P1): order_type(+ table_number) 까지 일치해야 재사용.
      // BE order_type 도메인(pickup/table)으로 정규화 (delivery → pickup 폴백).
      const cartSignature = computeCartSignature(cartItems);
      const orphanOrderType = SUPPORTED_ORDER_TYPES[orderType];
      const orphanRecord = getOrphanOrder(
        storeResult.storeId!,
        cartSignature,
        orphanOrderType,
        tableNumber,
      );
      let order: Awaited<ReturnType<typeof orderApi.createOrder>>['data'];

      if (orphanRecord !== null) {
        // 기존 orphan order 재사용 — payment 만 재시도.
        // R3 시정 (code-reviewer P1): saveGuestCredentials 호출 *금지*. orphan 생성
        // 시점에 이미 저장됐고, 사용자가 phone 을 수정한 경우 localStorage 의 phone
        // 과 BE 의 orphan 주문 phone 이 충돌 → guestLookup 401/403 회귀.
        logger.info('orphan order 재사용:', { order_id: orphanRecord.order_id });
        usedOrphanOrderId = orphanRecord.order_id;
        usedOrphanStoreId = storeResult.storeId!;
        usedOrphanCurrency = orphanRecord.currency;
        usedOrphanTotal = orphanRecord.total;
        order = {
          id: orphanRecord.order_id,
          total: orphanRecord.total,
          store_id: storeResult.storeId!,
          currency: orphanRecord.currency,  // rev 4 P2: createPaymentData 가 사용
        } as Awaited<ReturnType<typeof orderApi.createOrder>>['data'];
      } else {
        createdOrderResponse = await orderApi.createOrder(orderRequest);
        order = createdOrderResponse.data;
        logger.info('주문 생성 성공:', { order_id: order.id });

        // P1 stale guestLookup 시정 (codex R1 + code-reviewer R2 합의):
        // *신규* 주문 생성 성공 직후만 비회원 자격증명 저장. 결제 실패 catch 경로
        // 에서도 BE 에 주문이 남아있으므로 /orders 추적 가능해야 함. orphan 재사용
        // 분기는 위에서 별도 처리(저장 skip).
        // #647 C2 (codex R1): 빈 phone(전화 없는 후불 테이블)이어도 자격증명은 저장
        // 한다 — password 를 보존해야 세션 미결제 조회(GET unpaid-postpay 의
        // password 헤더)와 합산청구(POST charge 의 guest_password)가 admin
        // ResolvesTableSessionChargeAccess 에서 403 으로 차단되지 않는다.
        // 빈 phone 의 guestLookup 422 문제는 저장 skip 이 아니라 *phone 기반
        // lookup 호출* skip 으로 해결 (OrderHistoryContext.fetchGuestOrders /
        // useActiveOrders 가드). phone trim 정규화는 저장 경계(P2-1)도 수행.
        if (!isLoggedIn && guestPassword) {
          saveGuestCredentials(trimmedPhone, guestPassword);
        }
      }

      if (orderType === 'table' && resolvedCombination.timing === 'postpay') {
        const nowIso = new Date().toISOString();
        const postpaySnapshot: Payment = {
          id: 0,
          order_id: order.id,
          method: resolvedCombination.method,
          method_label: resolvedCombination.label_key,
          provider: 'manual',
          status: 'pending',
          status_label: 'Pending',
          amount: String(total),
          currency: order.currency,
          points_amount: 0,
          refund_amount: '0.00',
          refundable_amount: '0.00',
          is_paid: false,
          is_successful: false,
          is_refundable: false,
          is_cancellable: false,
          is_final: false,
          is_checkout_expired: false,
          requires_manual_confirmation: true,
          summary: resolvedCombination.code,
          paid_at: null,
          failed_at: null,
          refunded_at: null,
          created_at: nowIso,
          updated_at: nowIso,
        };
        const paymentData = createPaymentData(
          order,
          postpaySnapshot,
          guestPassword,
          cartItems,
          { net: netPrice, tax, tip: effectiveTipAmount, discount: pointsToUse },
          resolvedCombination,
        );
        const completedOrderData = {
          ...paymentData,
          tableNumber,
        };

        createOrderFromCheckout({
          orderData: completedOrderData,
          cartItems,
          orderedItems: [],
          restaurantName: completedOrderData.restaurant || '',
          isTableOrder: true,
          tableNumber: tableNumber ?? null,
        });
        clearOrphanOrder();
        setIsCompletingOrder(true);
        setJustOrderedSummary({ itemCount: cartItemCount, totalAmount: total, currency: 'MXN' });
        setTableMenuReturnPath(
          storeId !== undefined && tableNumber
            ? getRestaurantTablePath(String(storeId), tableNumber)
            : null,
        );
        clearCart();
        toast.success(t.orderCreated || 'Your order has been placed.');
        await navigateAfterCompletedOrder(order.id, 'postpay', guestPassword);
        return;
      }

      // 6. 결제 생성 API 호출 (β-2b: combination_id 기반).
      // admin `required_without` 계약상 method 는 더 이상 전송 안 해도 됨.
      // 安全: pointsToUse를 팁 적용 전 총액(grossPlusTip)으로 캡핑
      // overridePoints가 지정되면 해당 값 사용 (포인트 전액 모드에서 카드 전환 시 0)
      // A server price change can leave a positive card balance despite a zero local estimate.
      if (platformPrepayUnavailable && Number.isFinite(Number(order.total)) &&
        subtractMoney(Number(order.total), safePointsToUse) > 0) {
        if (createdOrderResponse) {
          saveOrphanOrder({
            order_id: order.id, store_id: order.store_id, total: Number(order.total),
            cart_signature: computeCartSignature(cartItems), currency: order.currency,
            order_type: SUPPORTED_ORDER_TYPES[orderType], table_number: tableNumber,
          });
        }
        toast.error(t.cardPaymentUnavailable);
        return;
      }
      const shouldSendSavedCardId = method === 'card'
        && isPlatformCardCombination(resolvedCombination);
      const paymentResponse = await paymentApi.createPayment(order.id, {
        combination_id: resolvedCombination.id,
        guest_password: guestPassword,
        points_amount: safePointsToUse > 0 ? safePointsToUse : undefined,
        saved_card_id: shouldSendSavedCardId && selectedCardId ? selectedCardId : undefined,
      });

      const payment = paymentResponse.data;
      logger.info('결제 생성 성공:', payment);
      const bloominChallengeUrl = paymentResponse.bloomin?.challenge_url;
      let trustedBloominChallengeUrl: string | null = null;

      if (paymentResponse.gateway === 'bloomin' && bloominChallengeUrl) {
        const challengeUrl = new URL(bloominChallengeUrl);
        if (!isTrustedBloominChallengeUrl(challengeUrl)) {
          throw new Error('Bloomin challenge URL is not trusted');
        }
        trustedBloominChallengeUrl = challengeUrl.toString();
      }

      // #311: 결제 성공 — orphan record 삭제 (cleanup)
      clearOrphanOrder();

      // 7. 결제 데이터 생성 및 저장
      const paymentData = createPaymentData(
        order,
        payment,
        guestPassword,
        cartItems,
        // #336 B: GROSS / TOTAL 직접 산술 제거 — createPaymentData 내부 factory 가
        // GROSS = NET + TAX, TOTAL = GROSS + TIP - DISCOUNT 강제.
        // #603 [4] 보강2: 유효팁(effectiveTipAmount) — hideOrderTimeTip 시 0.
        { net: netPrice, tax, tip: effectiveTipAmount, discount: pointsToUse },
        resolvedCombination,
      );
      const completedOrderData = {
        ...paymentData,
        tableNumber,
      };
      // codex C1: 로컬 주문 snapshot 은 주문 접수가 "확정" 된 분기에서만 저장한다.
      // 게이트웨이(3DS) redirect 경로에서 선저장하면 challenge 실패/취소/이탈 시
      // 주문내역에 정상 주문처럼 남는 ghost 가 생긴다 — 해당 경로는 API 조회에 위임.
      const saveOrderSnapshot = () =>
        createOrderFromCheckout({
          orderData: completedOrderData,
          cartItems,
          orderedItems: [],
          restaurantName: completedOrderData.restaurant || '',
          isTableOrder: orderType === 'table',
          tableNumber: tableNumber ?? null,
        });
      handlePaymentSuccess({
        ...completedOrderData,
        gateway: paymentResponse.gateway,
        bloominInitiateUrl: paymentResponse.bloomin?.initiate_url,
      });

      // 8. 결제 방식에 따른 분기 처리 (β-3b: combination.timing/channel 기반).
      // 순서: points-only → gateway (Bloomin 필수 값) → postpay → prepay_manual.
      // Codex R1 P1 반영: gateway 조합인데 Bloomin 진입 URL 누락 시 fall-through 차단 —
      // 결제 미완료 상태로 "주문 완료" navigate 되던 회귀 방지.
      const isPointsOnlyPayment = (payment.points_amount ?? 0) > 0 && parseFloat(payment.amount) === 0;
      if (isPointsOnlyPayment) {
        // 포인트 전액 결제: 즉시 완료 — 결제 처리 모달 건너뛰고 완료 라우트로 이동.
        saveOrderSnapshot();
        setIsCompletingOrder(true);
        // #603 [1]: clearCart 직전 "방금 주문" 요약 + 테이블 복귀 경로 캡처.
        setJustOrderedSummary({ itemCount: cartItemCount, totalAmount: total, currency: 'MXN' });
        setTableMenuReturnPath(
          orderType === 'table' && storeId !== undefined && tableNumber
            ? getRestaurantTablePath(String(storeId), tableNumber)
            : null,
        );
        clearCart();
        toast.success(t.orderCreated || 'Your order has been placed.');
        await navigateAfterCompletedOrder(order.id, undefined, guestPassword);
      } else if (isPlatformCardCombination(resolvedCombination)) {
        const hasBloominNextStep = paymentResponse.gateway === 'bloomin'
          && (Boolean(paymentResponse.bloomin?.initiate_url) || Boolean(bloominChallengeUrl));
        if (!hasBloominNextStep) {
          logger.error('card_platform_prepay 인데 gateway 진입 URL 누락:', payment);
          toast.error(t.orderFailed || 'Payment gateway unavailable.');
          return;
        }

        if (trustedBloominChallengeUrl) {
          window.location.assign(trustedBloominChallengeUrl);
          return;
        }

        navigate(ROUTES.PAYMENT);
      } else if (resolvedCombination.timing === 'postpay') {
        // β-3b: 후불 (card_pos_postpay / cash_manual_postpay) — 식후 카운터 결제 안내
        // #603 [1]: 테이블·픽업 모두 주문 완료 페이지 경유(기존 즉시 리다이렉트 제거).
        saveOrderSnapshot();
        setIsCompletingOrder(true);
        setJustOrderedSummary({ itemCount: cartItemCount, totalAmount: total, currency: 'MXN' });
        setTableMenuReturnPath(
          orderType === 'table' && storeId !== undefined && tableNumber
            ? getRestaurantTablePath(String(storeId), tableNumber)
            : null,
        );
        clearCart();
        toast.success(t.orderCreated || 'Your order has been placed.');
        await navigateAfterCompletedOrder(order.id, 'postpay', guestPassword);
      } else {
        // 선불 수기 (cash_manual_prepay / card_pos_prepay) — 직원 안내 배너
        // #603 [1]: 테이블·픽업 모두 주문 완료 페이지 경유(기존 즉시 리다이렉트 제거).
        saveOrderSnapshot();
        setIsCompletingOrder(true);
        setJustOrderedSummary({ itemCount: cartItemCount, totalAmount: total, currency: 'MXN' });
        setTableMenuReturnPath(
          orderType === 'table' && storeId !== undefined && tableNumber
            ? getRestaurantTablePath(String(storeId), tableNumber)
            : null,
        );
        clearCart();
        toast.success(t.orderCreated || 'Your order has been placed.');
        await navigateAfterCompletedOrder(order.id, 'prepay_manual', guestPassword);
      }
    } catch (error) {
      logger.error('주문/결제 처리 실패:', error);

      // #311: orphan order 가드.
      // rev 4 hot-fix (Codex P1): error_code → ApiClientError.code 정정.
      // BE expire / 권한 위반 / 인증 실패 → record 삭제 (자연 복구). 그 외 + createOrder 성공 또는 재사용 경로 → record 저장 (TTL 갱신).
      const isApiError = error instanceof ApiClientError;
      const isOrderNotPayable =
        isApiError && error.status === 400 && error.code === 'ORDER_NOT_PAYABLE';
      const isAuthOrOwnershipFailure =
        isApiError && (error.status === 401 || error.status === 403 || error.status === 404);

      // #1705 R7 (Codex P1): record 저장 시 order_type(+ table_number) 박제 — 재사용 매칭용.
      const orphanOrderType = SUPPORTED_ORDER_TYPES[orderType];

      if (isOrderNotPayable || isAuthOrOwnershipFailure) {
        clearOrphanOrder();
      } else if (createdOrderResponse !== undefined) {
        // order 가 방금 생성됐고 payment 만 실패한 케이스 — record 저장
        const cartSignature = computeCartSignature(cartItems);
        saveOrphanOrder({
          order_id: createdOrderResponse.data.id,
          total: createdOrderResponse.data.total,
          store_id: createdOrderResponse.data.store_id,
          cart_signature: cartSignature,
          currency: createdOrderResponse.data.currency,
          order_type: orphanOrderType,
          table_number: tableNumber,
        });
      } else if (
        usedOrphanOrderId !== undefined &&
        usedOrphanStoreId !== undefined &&
        usedOrphanCurrency !== undefined
      ) {
        // rev 4 hot-fix (code-reviewer P1): orphan 재사용 경로 payment 실패 → TTL 갱신
        const cartSignature = computeCartSignature(cartItems);
        saveOrphanOrder({
          order_id: usedOrphanOrderId,
          total: usedOrphanTotal,
          store_id: usedOrphanStoreId,
          cart_signature: cartSignature,
          currency: usedOrphanCurrency,
          order_type: orphanOrderType,
          table_number: tableNumber,
        });
      }

      if (shouldPromptLoginForAuthExpiry(error, isLoggedIn)) {
        // Reauth resumes the current checkout route only; card inputs are never persisted.
        setShowLoginPrompt(true);
        return;
      }

      const errorMessage = getCheckoutErrorMessage(error, t);
      toast.error(errorMessage);

      // #804 방어: RestaurantPage 의 게이트 CTA 를 우회해 도달한 픽업 주문은 서버가 최종
      // 판정한다. 매장 메뉴로 되돌려 게이트를 재평가받게 한다.
      //
      // admin 설계 §3.2 보정(2026-07-24) — 파티 전이(called → preparing)는 주문 생성이
      // 아니라 "결제 성공" 시점으로 이연된다. 따라서 주문 생성~결제 완료 사이에는 파티가
      // 여전히 called 상태이고, 이 구간에 매장 페이지로 돌아와 재주문을 시도하면
      // WAITLIST_NOT_CALLED 422 가 발생하되 그 실제 원인은 "아직 호출되지 않음"이 아니라
      // "이미 생성된 주문의 결제가 남아있음"(활성 주문 존재)이다. 두 원인이 동일 코드를
      // 공유하므로 클라이언트에서 컨텍스트로 분기하지 않고, 서버가 lang errors 로 구분해
      // 내려주는 message 를 그대로 신뢰한다(getCheckoutErrorMessage 의 isServerMessage
      // 우선순위 — 위 toast.error(errorMessage) 가 이미 그 메시지를 노출한다). 서버가
      // message 를 내려주지 않는 방어적 상황에서만 기존 CTA(줄서기 하러 가기 유도)로
      // 폴백한다.
      if (
        isApiError &&
        (error.code === 'WAITLIST_REQUIRED' || error.code === 'WAITLIST_NOT_CALLED') &&
        storeId !== undefined
      ) {
        navigate(getRestaurantPath(String(storeId)), { replace: true });
      }
    } finally {
      paymentLockRef.current = false;
      setIsSubmitting(false);
    }
  }, [
    name,
    phone,
    address,
    orderType,
    cartItems,
    netPrice,
    tax,
    effectiveTipAmount,
    grossPlusTip,
    platformCardPrepayAvailability,
    pointsToUse,
    selectedCardId,
    deliveryNotes,
    tableNumber,
    tableSessionState,
    storeId,
    total,
    cartItemCount,
    isSubmitting,
    isLoggedIn,
    useAlternateContact,
    contactPhone,
    handlePaymentSuccess,
    navigateAfterCompletedOrder,
    clearCart,
    navigate,
    t,
    availableCombinations,
    saveGuestCredentials,
    createOrderFromCheckout,
  ]);

  // 카드 결제
  const handlePayByCard = useCallback(async () => {
    await handleProceedToPayment('card');
  }, [handleProceedToPayment]);

  // 현금 결제
  const handlePayByCash = useCallback(async () => {
    await handleProceedToPayment('cash');
  }, [handleProceedToPayment]);

  // #666: 후불 테이블 단일 '주문하기' — 내부 기본 method(defaultPostpayMethod)로 주문 접수.
  // 수단은 결제 시점에 확정되므로 여기서의 method 는 서버 계약용 기본 조합 힌트일 뿐이다.
  const handlePlaceOrder = useCallback(async () => {
    await handleProceedToPayment(defaultPostpayMethod);
  }, [handleProceedToPayment, defaultPostpayMethod]);

  // 포인트 전액 결제 (BE에서 card + points_amount >= total → 게이트웨이 생략)
  const handlePayByPoints = useCallback(async () => {
    await handleProceedToPayment('card');
  }, [handleProceedToPayment]);

  // 포인트 전액 모드에서 카드 결제 전환 (포인트 0으로 강제)
  const handlePayByCardWithoutPoints = useCallback(async () => {
    setPointsToUse(0);
    await handleProceedToPayment('card', 0);
  }, [handleProceedToPayment]);

  // 카드 추가 모달 열기
  const handleAddNewCard = useCallback(() => {
    setIsAddCardModalOpen(true);
  }, []);

  // 카드 추가 모달 닫기
  const handleCloseAddCardModal = useCallback(() => {
    setIsAddCardModalOpen(false);
  }, []);

  // 카드 등록 성공
  const handleCardRegistrationSuccess = useCallback((newCard: SavedCard) => {
    setSelectedCardId(newCard.id);
    setIsAddCardModalOpen(false);
    navigate(window.location.pathname, { replace: true });
  }, [navigate]);

  return (
    <div className="min-h-dvh bg-muted">
      {/* 헤더 */}
      <div className="sticky top-0 z-10 bg-white shadow-sm">
        <div className="flex items-center px-[16px] h-[64px]">
          <button
            onClick={handleBack}
            className="p-[8px] -ml-[8px] text-foreground hover:bg-muted rounded-[5px] transition-colors"
            aria-label={t.cancel || 'Go back'}
          >
            <ArrowLeft className="w-[24px] h-[24px]" aria-hidden="true" />
          </button>
          <h1 className="flex-1 text-center text-[18px] font-bold text-foreground pr-[40px]">
            {t.title}
          </h1>
        </div>
      </div>

      <div className="max-w-[600px] mx-auto p-[16px] pb-[180px]">
        {/* 주문 항목 */}
        <section className="bg-white rounded-[10px] p-[20px] mb-[16px] shadow-xs">
          <h2 className="text-[16px] font-bold mb-[16px]">{t.orderSummary}</h2>
          <CheckoutItemsList
            items={cartItems}
            optionToggle={{ show: t.viewOptions, hide: t.hideOptions }}
          />
        </section>

        {/* #363: 테이블 오더 시 테이블 정보, 그 외 픽업 정보 */}
        <section className="bg-white rounded-[10px] p-[20px] mb-[16px] shadow-xs">
          <h2 className="text-[16px] font-bold mb-[16px]">
            {orderType === 'table' ? t.tableInfo : t.pickupInfo}
          </h2>
          {orderType === 'table' && tableNumber && (
            <div className="mb-[16px] px-[12px] py-[10px] bg-muted rounded-[5px] flex items-center justify-between">
              <span className="text-[14px] text-muted-foreground">{t.tableNumber}</span>
              <span className="text-[14px] font-bold text-foreground">{tableNumber}</span>
            </div>
          )}
          <div className="space-y-[12px]">
            <div>
              <div className="flex items-center justify-between mb-[8px]">
                <label htmlFor="checkout-name" className="text-[14px] text-muted-foreground">
                  {t.name}{orderType === 'table' ? ` (${t.optional})` : ''}
                </label>
                {!isLoggedIn && (
                  <button
                    onClick={() => navigate(ROUTES.LOGIN)}
                    className="text-[12px] text-primary underline"
                  >
                    {t.loginLink}
                  </button>
                )}
              </div>
              <input
                id="checkout-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-[16px] py-[12px] border border-border rounded-[5px] text-[14px] focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder={t.name}
                // #628 F-03: 테이블 주문은 QR 신원이라 이름이 선택 — required 해제.
                required={orderType !== 'table'}
                aria-required={orderType !== 'table'}
              />
            </div>
            <div>
              <label htmlFor="checkout-phone" className="block text-[14px] text-muted-foreground mb-[8px]">
                {t.phone}{orderType === 'table' ? ` (${t.optional})` : ''}
              </label>
              <input
                id="checkout-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                readOnly={isLoggedIn}
                aria-readonly={isLoggedIn}
                className={`w-full px-[16px] py-[12px] border border-border rounded-[5px] text-[14px] focus:outline-none focus:ring-2 focus:ring-primary ${isLoggedIn ? 'bg-muted text-muted-foreground' : ''}`}
                placeholder={t.phone}
                required
                aria-required="true"
              />
              {isLoggedIn && (
                <p className="mt-[6px] text-[12px] text-muted-foreground">{t.phoneReadOnlyHint}</p>
              )}
            </div>
            {/* #272 Q8 옵션 B: 로그인 사용자 전용 대체 연락처 토글 */}
            {isLoggedIn && (
              <div>
                <label className="flex items-center gap-[8px] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useAlternateContact}
                    onChange={(e) => setUseAlternateContact(e.target.checked)}
                    className="w-[16px] h-[16px] accent-primary"
                    aria-controls="checkout-contact-phone"
                    aria-expanded={useAlternateContact}
                  />
                  <span className="text-[14px] text-foreground">{t.useAlternateContact}</span>
                </label>
                {useAlternateContact && (
                  <div className="mt-[8px]">
                    <label htmlFor="checkout-contact-phone" className="block text-[14px] text-muted-foreground mb-[8px]">
                      {t.alternateContactPhoneLabel}
                    </label>
                    <input
                      id="checkout-contact-phone"
                      type="tel"
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                      className="w-full px-[16px] py-[12px] border border-border rounded-[5px] text-[14px] focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder={t.alternateContactPhonePlaceholder}
                      maxLength={20}
                    />
                    <p className="mt-[6px] text-[12px] text-muted-foreground">{t.alternateContactPhoneHint}</p>
                  </div>
                )}
              </div>
            )}
            <div>
              <label htmlFor="checkout-notes" className="block text-[14px] text-muted-foreground mb-[8px]">{t.deliveryNotes}</label>
              <textarea
                id="checkout-notes"
                value={deliveryNotes}
                onChange={(e) => setDeliveryNotes(e.target.value)}
                className="w-full px-[16px] py-[12px] border border-border rounded-[5px] text-[14px] focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                placeholder={t.deliveryNotesPlaceholder}
                rows={2}
              />
            </div>
          </div>
        </section>

        {/* 팁 선택 — #603 [4]: 후불 테이블오더는 식후 세션 결제로 이동(주문시점 미노출). 선불/픽업 유지 */}
        {!hideOrderTimeTip && (
          <section className="bg-white rounded-[10px] p-[20px] mb-[16px] shadow-xs">
            <TipSelector
              selectedTip={selectedTip}
              customTip={customTip}
              customTipMode={customTipMode}
              onSelectTip={setSelectedTip}
              onCustomTip={(value, mode) => {
                setCustomTip(value);
                setCustomTipMode(mode);
              }}
              tipAmount={effectiveTipAmount}
              label={t.tipLabel}
              customLabel={t.tipCustom}
              tipAmountLabel={t.tipAmount}
              formatCurrency={(n) => formatCurrency(n, 'MXN')}
              calculateSubtotal={calculateSubtotal}
              modalT={{
                enterTip: t.tipEnterTip,
                percentage: t.tipPercentage,
                amount: t.tipAmountMode,
                enterTipPercentage: t.tipEnterTipPercentage,
                enterTipAmount: t.tipEnterTipAmount,
                estimatedTip: t.tipEstimated,
                cancel: t.tipCancel,
                apply: t.tipApply,
              }}
            />
          </section>
        )}

        {/* 금액 요약 */}
        <section className="bg-white rounded-[10px] p-[20px] mb-[16px] shadow-xs">
          <CheckoutSummary
            netPrice={netPrice}
            tax={tax}
            tipAmount={effectiveTipAmount}
            pointsDiscount={pointsToUse}
            total={total}
            formatCurrency={(n) => formatCurrency(n, 'MXN')}
            estimatedPoints={pointsEnabled ? estimatedPointsData?.estimatedPoints : undefined}
            isEstimating={pointsEnabled && isEstimating}
            expiryDays={pointsConfig?.expiry_days}
            labels={{
              netPrice: t.netPrice,
              taxIVA: t.taxIVA,
              tip: t.tipAmount,
              pointsDiscount: t.pointsSection,
              total: t.total,
              estimatedPoints: t.estimatedPoints,
              estimating: t.estimating,
            }}
          />
        </section>

        {isPostpayOnlyStore && (
          <section
            className="mb-[16px] rounded-[8px] border border-amber-200 bg-amber-50 px-[16px] py-[14px]"
            aria-label={t.postpayCheckoutNoticeTitle}
          >
            <div className="flex gap-[10px]">
              <Info className="mt-[1px] h-[18px] w-[18px] flex-shrink-0 text-amber-700" aria-hidden="true" />
              <div>
                <h2 className="text-[14px] font-bold text-amber-950">
                  {t.postpayCheckoutNoticeTitle}
                </h2>
                <p className="mt-[4px] text-[13px] leading-[18px] text-amber-900">
                  {t.postpayCheckoutNoticeDescription}
                </p>
              </div>
            </div>
          </section>
        )}

        {/* 포인트 사용 — #285 매장 정책 + #312 prepay 조합 존재 시만 노출 (postpay 전용 매장 차단) */}
        {pointsEnabled && hasPrepayOption && (
        <section className="bg-white rounded-[10px] p-[20px] mb-[16px] shadow-xs">
          <PointsSection
            disabled={!isLoggedIn || !pointsData || pointsLoading}
            totalAvailable={pointsData?.totalAvailable ?? 0}
            usableAmount={pointsData?.usableAmount ?? 0}
            minUsePoints={pointsData?.minUsePoints ?? 0}
            maxPoints={grossPlusTip}
            onPointsChange={setPointsToUse}
            pointsToUse={pointsToUse}
            formatCurrency={(n) => formatCurrency(n, 'MXN')}
            loginPromptLabel={
              !isLoggedIn
                ? t.loginToSeePoints
                : pointsError
                  ? t.pointsLoadError
                  : undefined
            }
            labels={{
              sectionTitle: t.pointsSection,
              available: t.pointsAvailable,
              usable: t.pointsUsable,
              placeholder: t.pointsToUse,
              apply: t.pointsApply,
              useAll: t.pointsUseAll,
              discountApplied: t.pointsDiscountApplied,
              minAmount: t.pointsMinAmount,
            }}
          />
        </section>
        )}
      </div>

      {/* 하단 고정 카드 선택 + 결제 버튼 */}
      <SavedCardBottomPanel
        savedCards={isLoggedIn ? usableCards : []}
        selectedCardId={selectedCardId}
        onCardSelect={setSelectedCardId}
        onAddNewCard={handleAddNewCard}
        onPayCash={handlePayByCash}
        onPayCard={handlePayByCard}
        isSubmitting={isSubmitting}
        isLoggedIn={isLoggedIn}
        isLoadingCards={isLoadingCards}
        totalLabel={cardCtaLabel}
        cashLabel={cashCtaLabel}
        addCardLabel={t.addNewCard}
        noCardsLabel={t.noSavedCards}
        cardsLabel={t.savedCards}
        defaultLabel={t.defaultCard}
        formatCurrency={(n) => formatCurrency(n, 'MXN')}
        totalAmount={total}
        safeAreaBottom={safeAreaBottom}
        isPointsFullCover={isPointsFullCover}
        onPayPoints={handlePayByPoints}
        onPayCardWithoutPoints={handlePayByCardWithoutPoints}
        pointsPayLabel={t.pointsPayment}
        cardPaymentAltLabel={t.cardPaymentAlt}
        selectCardHintLabel={t.selectPaymentMethod}
        cashSupported={cashSupported}
        cardSupported={cardSupported}
        cashUnavailableLabel={t.cashPaymentUnavailable}
        cardUnavailableLabel={t.cardPaymentUnavailable}
        isLoadingPaymentMethods={isLoadingPaymentMethods}
        requiresCardSelection={!isPostpayOnlyStore}
        singleOrderMode={isPostpayTableCheckout}
        onPlaceOrder={handlePlaceOrder}
        orderLabel={t.postpayCheckoutButton}
      />

      {/* AddCardModal */}
      <AddCardModal
        isOpen={isAddCardModalOpen}
        checkout={checkout}
        isCreatingCheckout={isCreatingCheckout}
        isConfirming={isConfirming}
        onClose={handleCloseAddCardModal}
        onCreateCheckout={createCheckout}
        onConfirmBloominRegistration={async (
          cardPayload: BloominCardRegistrationPayload,
          nickname
        ) => {
          const newCard = await confirmBloominRegistration(cardPayload, nickname);
          handleCardRegistrationSuccess(newCard);
        }}
        t={{
          title: addCardT.addCardTitle,
          subtitle: addCardT.addCardSubtitle,
          loading: addCardT.loading,
          creatingCheckout: addCardT.creatingCheckout,
          confirmingRegistration: addCardT.confirmingRegistration,
          successTitle: addCardT.successTitle,
          successMessage: addCardT.successMessage,
          errorTitle: addCardT.errorTitle,
          errorMessage: addCardT.errorMessage,
          retry: addCardT.retry,
          done: addCardT.done,
          cancel: addCardT.cancel,
          nicknameLabel: addCardT.nicknameLabel,
          nicknamePlaceholder: addCardT.nicknamePlaceholder,
          skipNickname: addCardT.skipNickname,
          saveWithNickname: addCardT.saveWithNickname,
          cardNumberLabel: addCardT.cardNumberLabel,
          expiryMonthLabel: addCardT.expiryMonthLabel,
          expiryYearLabel: addCardT.expiryYearLabel,
          cvvLabel: addCardT.cvvLabel,
          holderLabel: addCardT.holderLabel,
          invalidCard: addCardT.invalidCard,
          invalidExpiry: addCardT.invalidExpiry,
          invalidCvv: addCardT.invalidCvv,
          invalidHolder: addCardT.invalidHolder,
        }}
      />

      <LoginPromptSheet
        isOpen={showLoginPrompt}
        onLogin={handleGoLogin}
        onContinueAsGuest={handleDismissLoginPrompt}
        t={{
          title: t.loginPromptTitle,
          benefit1: t.loginPromptBenefit1,
          benefit2: t.loginPromptBenefit2,
          benefit3: t.loginPromptBenefit3,
          login: t.loginPromptLogin,
          continueAsGuest: t.loginPromptContinueAsGuest,
          closeAriaLabel: a11yTranslations[locale].close,
        }}
      />

      {showOrderSuccess && (
        <OrderSuccessScreen
          orderType={orderType === 'table' ? 'table' : 'pickup'}
          tableNumber={tableNumber}
          justOrderedSummary={justOrderedSummary ?? undefined}
          formatCurrency={formatCurrency}
          labels={{
            title: t.orderSuccessTitle,
            thanks: t.orderSuccessThanks,
            justOrderedPrefix: t.orderSuccessJustOrderedPrefix,
            itemCountUnit: t.orderSuccessItemCountUnit,
            tableLabel: (n) => t.orderSuccessTableLabel.replace('{number}', n),
            viewMenu: t.orderSuccessViewMenu,
            viewOrders: t.orderSuccessViewOrders,
            goHome: t.orderSuccessGoHome,
          }}
          pushOptInLabels={{
            title: t.pushOptInTitle,
            accept: t.pushOptInAccept,
            later: t.pushOptInLater,
            // #694 게스트 전용 문구 — 비로그인 + guestContext 시 사용.
            guestTitle: t.pushOptInGuestTitle,
            loginHint: t.pushOptInLoginHint,
          }}
          pushGuestContext={guestPushContext ?? undefined}
          // #626: 테이블 — 5초 후 메뉴 자동 복귀(추가 주문 유도) + secondary(주문 현황 보기) 제거.
          // codex R1 Critical: 복귀 경로(tableMenuReturnPath) 캡처 실패 시 자동 이동 비활성화 —
          // 자동으로 주문내역(폴백)으로 끌려가면 #626 목적(메뉴 복귀) 위배. 수동 클릭 폴백만 유지.
          autoNavigateSeconds={orderType === 'table' && tableMenuReturnPath ? 5 : undefined}
          onPrimary={orderType === 'table' ? handleContinueTableOrder : handleViewCompletedOrders}
          onSecondary={orderType === 'table' ? undefined : handleGoHome}
        />
      )}

    </div>
  );
}

/**
 * CheckoutPage with ErrorBoundary wrapper
 *
 * 결제 페이지에서 예상치 못한 렌더링 에러 발생 시
 * 전체 앱 크래시를 방지하고 사용자 친화적인 에러 화면을 제공합니다.
 *
 * @see #132 - CheckoutPage에 React ErrorBoundary 추가
 */
export function CheckoutPageWithErrorBoundary() {
  return (
    <ErrorBoundary
      fallback={({ error, resetError }) => (
        <CheckoutErrorFallback error={error} onReset={resetError} />
      )}
    >
      <CheckoutPage />
    </ErrorBoundary>
  );
}
